import { useCallback, useEffect, useState } from 'react';
import {
    FinancingApi,
    SubmitLoanApiRequest,
    SubmitLoanApiResponse,
    resolveProviderEnum,
} from '@servicetitan/financing-api-client';
import {
    ApplicationDataStore,
    prepareQualificationData,
    mapServiceFinanceImprovementToIndustryId,
} from '@servicetitan/financing-application-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    SetupDataStore,
    WorkflowStore,
    SecondLookApplicationSubmittedStore,
    useTenantSettings,
    useSecondLookIntegration,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import {
    ServerError,
    failSecondLookAttempt,
    identifySecondLookAttempt,
    startSecondLookAttempt,
} from '@servicetitan/financing-utils-shared';
import { FinancedAmountStore } from '@servicetitan/financing-plans-shared';

export interface UseSecondLookSubmissionOptions {
    /** Whether to automatically submit on mount (default: true) */
    autoSubmit?: boolean;
    /** Custom success callback - called when submission is successful */
    onSuccess?: (data: SubmitLoanApiResponse) => void;
    /** Custom error callback */
    onError?: (error: ServerError) => void;
}

export interface UseSecondLookSubmissionReturn {
    /** Submit the second look application manually */
    submitSecondLook: () => void;
    /** Whether the application is currently being submitted */
    isSubmitting: boolean;
    /** Whether the application has been submitted successfully */
    isSuccess: boolean;
    /** Any error that occurred during submission */

    error: ServerError | null;
    /** Current loan ID from submission */
    loanId: string | null;
    /** Whether IP address has been fetched */
    hasAttemptedIpFetch: boolean;
    /** Current IP address */
    ipAddress: string | null;
    /** Whether application has been submitted */
    hasSubmittedApplication: boolean;
    /** Whether the requested amount is below the waterfall minimum threshold (only meaningful when tenantSettingsLoaded is true) */
    isBelowWaterfallThreshold: boolean;
    /** Whether tenant settings have finished loading */
    tenantSettingsLoaded: boolean;
    /** Reset the submission state */
    reset: () => void;
}

export const useSecondLookSubmission = (
    options: UseSecondLookSubmissionOptions = {},
): UseSecondLookSubmissionReturn => {
    const { autoSubmit = true, onSuccess, onError } = options;

    const [financingApi] = useDependencies(FinancingApi);
    const { applicationData } = ApplicationDataStore.useStore();
    const { workflowStage, setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { decisionData } = DecisionDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setIsSecondLookApplicationSubmitted } = SecondLookApplicationSubmittedStore.useStore();
    const tenantSettings = useTenantSettings();
    const { secondLookProvider } = useSecondLookIntegration();
    const { hasMailingAddressStep } = useProviderConfig();

    // The provider to submit the second look against, resolved from the tenant's configuration.
    const effectiveProviderName = secondLookProvider ?? undefined;

    // Which second-look path this submission came from. The three waterfall entry points all
    // submit from the reviewing stage, so `provider` is what separates them from one another.
    const secondLookEntry =
        workflowStage === 'second-look-submitting'
            ? setupData?.isInSecondLook
                ? 'manual'
                : 'primary'
            : 'waterfall';

    // Track whether the tenant settings query has settled (success or failure).
    // useTenantSettings returns undefined for both "loading" and "error", so we
    // query the same cache key to get the full state without an extra network call.
    const tenantSettingsQuery = useQuery({
        queryKey: ['tenantSettings', setupData?.tenantId],
        queryFn: () => financingApi.tenantsGetSettings(setupData?.tenantId ?? ''),
        enabled: !!setupData?.tenantId,
        staleTime: 5 * 60 * 1000,
    });
    const tenantSettingsLoaded = tenantSettingsQuery.isSuccess || tenantSettingsQuery.isError;

    const waterfallMinThreshold = tenantSettings?.secondLookMinRequestedAmount ?? null;
    const isBelowWaterfallThreshold =
        tenantSettingsLoaded &&
        waterfallMinThreshold != null &&
        financedAmount != null &&
        financedAmount < waterfallMinThreshold;

    const previousLoanId = decisionData?.loanId;
    const [ipAddress, setIpAddress] = useState<string | null>(null);
    const [hasAttemptedIpFetch, setHasAttemptedIpFetch] = useState(false);
    const [ipFetchTimeout, setIpFetchTimeout] = useState(false);
    const [loanId, setLoanId] = useState<string | null>(null);
    const [hasSubmittedApplication, setHasSubmittedApplication] = useState(false);

    // Check if we need to map a ServiceFinance improvement type to the second-look provider's
    // industry catalog. This applies to all second-look providers (Turns, Finturf, etc.) because
    // ServiceFinance collects an improvement type that must be translated to the target provider's
    // industry taxonomy before submission.
    const industryType = applicationData.applicant?.primaryApplicant?.jobDetails?.industryType;
    const improvement =
        applicationData.serviceProperty?.improvement ??
        (applicationData.applicant as any)?.improvement;
    const effectiveProvider = effectiveProviderName
        ? resolveProviderEnum(effectiveProviderName)
        : undefined;
    const isServiceFinanceToSecondLookFlow =
        setupData?.provider?.toLowerCase() === 'servicefinance' && !!effectiveProviderName;
    const isFinturfSecondLook = effectiveProviderName?.toLowerCase() === 'finturf';
    const needsImprovementMapping =
        isServiceFinanceToSecondLookFlow &&
        !industryType &&
        improvement != null &&
        !isFinturfSecondLook;
    const sendSourceImprovement =
        isServiceFinanceToSecondLookFlow &&
        isFinturfSecondLook &&
        !industryType &&
        improvement != null;

    // Fetch the second-look provider's industry catalog for improvement-to-industry mapping.
    // Resolve to the enum value (e.g. 'turns', 'finturf') before keying the query so this cache
    // entry is shared with IndustryTypeCombobox which also resolves via resolveProviderEnum.
    // Using the raw PascalCase name here would produce a different key and cause a duplicate fetch.
    const buId = setupData?.buId ?? undefined;
    const industriesQuery = useQuery({
        queryKey: ['industryTypes', effectiveProvider, buId],
        // Store response.data (GetCatalogApiResponse) so the cache shape matches
        // IndustryTypeCombobox, which uses the same key and also stores response.data.
        queryFn: async () => {
            const response = await financingApi.providersGetCatalog({
                provider: effectiveProvider!,
                catalogName: 'Industries',
                businessUnitId: buId,
            });
            return response.data;
        },
        enabled: needsImprovementMapping,
    });
    const industriesData = industriesQuery.data;

    const mutation = useMutation<SubmitLoanApiResponse, ServerError, SubmitLoanApiRequest>({
        mutationKey: ['submitSecondLook'],
        mutationFn: async (data: SubmitLoanApiRequest) => {
            const response = await financingApi.loansSubmitApplication(data);
            return response.data;
        },
        onMutate: () => {
            startSecondLookAttempt({
                provider: setupData?.provider,
                experience: setupData?.role,
                secondLookProvider: effectiveProviderName,
                entry: secondLookEntry,
            });
        },
        onSuccess: response => {
            if (response.financingId) {
                identifySecondLookAttempt(response.financingId);
                setLoanId(response.financingId);
                setHasSubmittedApplication(true);
                setIsSecondLookApplicationSubmitted(true);
                if (onSuccess) {
                    onSuccess(response);
                }
            } else {
                // A 2xx with no loan to poll leaves nothing to wait on, so from the customer's
                // side the attempt failed just as surely as a rejected request.
                failSecondLookAttempt();
                setWorkflowStage('application');
            }
        },
        onError: error => {
            failSecondLookAttempt();
            setHasSubmittedApplication(false);
            if (onError) {
                onError(error);
            }
        },
    });

    // Destructure mutate function for stable reference in useCallback
    const { mutate } = mutation;

    // Fetch IP address when component mounts
    useEffect(() => {
        const fetchPublicIP = async () => {
            if (!hasAttemptedIpFetch) {
                try {
                    const response = await fetch('https://api.ipify.org?format=json');
                    const data = await response.json();
                    setIpAddress(data.ip);
                } catch {
                    setIpAddress(null);
                } finally {
                    setHasAttemptedIpFetch(true);
                }
            }
        };

        fetchPublicIP();
    }, [hasAttemptedIpFetch]);

    // Set timeout to proceed without IP address after 10 seconds
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setIpFetchTimeout(true);
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, []);

    const submitSecondLook = useCallback(() => {
        if (!tenantSettingsLoaded || isBelowWaterfallThreshold) {
            return;
        }

        // Don't submit if
        // 1. We haven't loaded the IP address yet OR the IP fetch timeout hasn't occurred
        // 2. We haven't loaded the industries data if we need to load it for improvement->industry mapping
        if (
            !hasAttemptedIpFetch ||
            (ipAddress === null && !ipFetchTimeout) ||
            (needsImprovementMapping && (!industriesQuery.isSuccess || !industriesData?.items))
        ) {
            return;
        }

        // If IP is still null after both fetch attempt and timeout, proceed anyway and
        // let the back-end throw an error that IP address is required.
        if (!hasSubmittedApplication && !loanId) {
            // Map ServiceFinance improvement to the second-look provider's industry ID - only when needed
            let mappedIndustryId: string | undefined;
            if (needsImprovementMapping && industriesData?.items) {
                const industries = industriesData.items
                    .filter(
                        (item): item is { key: string; displayName: string } =>
                            item.key != null && item.displayName != null,
                    )
                    .map(item => ({ key: item.key!, displayName: item.displayName! }));
                mappedIndustryId = mapServiceFinanceImprovementToIndustryId(
                    improvement,
                    industries,
                );
            }

            const application = prepareQualificationData({
                applicationData,
                tenantId: setupData!.tenantId,
                providerName: resolveProviderEnum(effectiveProviderName ?? setupData!.provider),
                buId: setupData!.buId,
                technicianId: setupData?.technicianId,
                jobId: setupData?.jobId ?? null,
                customerId: setupData!.customerId,
                userContext: {
                    ipAddress,
                    userAgent: window.navigator.userAgent,
                },
                previousLoanId: previousLoanId ?? undefined,
                industryIdFromMapping: mappedIndustryId,
                mappingSource: sendSourceImprovement ? 'ServiceFinance' : undefined,
                sourceImprovement: sendSourceImprovement ? improvement : undefined,
                hasMailingAddressStep,
            });
            application.requestedAmount = !application.requestedAmount
                ? financedAmount
                : application.requestedAmount;
            setHasSubmittedApplication(true); // Set flag before submission to prevent multiple calls
            mutate(application);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        hasAttemptedIpFetch,
        ipAddress,
        ipFetchTimeout,
        needsImprovementMapping,
        industriesQuery.isSuccess,
        industriesData?.items,
        hasSubmittedApplication,
        loanId,
        improvement,
        applicationData,
        setupData,
        effectiveProviderName,
        effectiveProvider,
        isServiceFinanceToSecondLookFlow,
        isFinturfSecondLook,
        sendSourceImprovement,
        previousLoanId,
        financedAmount,
        hasMailingAddressStep,
    ]);

    // Auto-submit application after IP fetch attempt or timeout
    useEffect(() => {
        // Auto-submit if either:
        // 1. IP address has been successfully fetched (ipAddress !== null)
        // 2. IP fetch timeout has occurred (after 10 seconds)
        // Also ensure industries data is loaded if it's needed for mapping
        const canSubmit =
            autoSubmit &&
            tenantSettingsLoaded &&
            !isBelowWaterfallThreshold &&
            (hasAttemptedIpFetch || ipFetchTimeout) &&
            (ipAddress !== null || ipFetchTimeout) &&
            !hasSubmittedApplication &&
            (!needsImprovementMapping || (industriesQuery.isSuccess && !!industriesData?.items)) &&
            !loanId;

        if (canSubmit) {
            submitSecondLook();
        }
    }, [
        autoSubmit,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        hasAttemptedIpFetch,
        ipFetchTimeout,
        hasSubmittedApplication,
        loanId,
        applicationData,
        setupData,
        ipAddress,
        previousLoanId,
        needsImprovementMapping,
        industriesQuery.isSuccess,
        industriesData?.items,
        submitSecondLook,
    ]);

    const reset = () => {
        setHasSubmittedApplication(false);
        setLoanId(null);
        setIpAddress(null);
        setHasAttemptedIpFetch(false);
        setIpFetchTimeout(false);
        mutation.reset();
    };

    return {
        submitSecondLook,
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,

        error: mutation.error as ServerError | null,
        loanId,
        hasAttemptedIpFetch,
        ipAddress,
        hasSubmittedApplication,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
        reset,
    };
};
